import type { Components, Theme } from "@mui/material";

/**
 * Per-component MUI overrides that establish the Material 3 / Robinhood feel.
 * Kept centralized here so individual pages never style these components
 * inline — they inherit from the theme.
 */

export const components: Components<Theme> = {
  MuiCssBaseline: {
    styleOverrides: (theme) => ({
      "*, *::before, *::after": { boxSizing: "border-box" },
      "html, body, #root": { minHeight: "100%" },
      body: {
        backgroundColor: theme.palette.background.default,
        color: theme.palette.text.primary,
        fontFeatureSettings: '"cv11" 1',
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale"
      },
      "::selection": {
        background: theme.palette.primary.main,
        color: theme.palette.primary.contrastText
      },
      "::-webkit-scrollbar": { width: 10, height: 10 },
      "::-webkit-scrollbar-track": { background: "transparent" },
      "::-webkit-scrollbar-thumb": {
        background: theme.palette.mode === "dark" ? "#1c2a47" : "#cbd3e2",
        borderRadius: 9999
      },
      "::-webkit-scrollbar-thumb:hover": {
        background: theme.palette.mode === "dark" ? "#2a3550" : "#9aa6c0"
      }
    })
  },

  MuiPaper: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundImage: "none",
        backgroundColor: theme.palette.background.paper
      })
    }
  },

  MuiCard: {
    defaultProps: { elevation: 0 },
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: 24,
        padding: theme.spacing(2.5),
        backgroundColor: theme.palette.mode === "dark" ? "#11192a" : "#ffffff",
        boxShadow: "none",
        border: "none"
      })
    }
  },

  MuiButton: {
    defaultProps: { disableElevation: true },
    styleOverrides: {
      root: {
        borderRadius: 9999,
        paddingInline: 20,
        paddingBlock: 10,
        fontWeight: 600,
        minHeight: 44
      },
      sizeSmall:  { paddingInline: 14, paddingBlock: 6,  minHeight: 36, fontSize: "0.8125rem" },
      sizeLarge:  { paddingInline: 26, paddingBlock: 14, minHeight: 52, fontSize: "1.0625rem" },
      contained: ({ theme }) => ({
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        boxShadow: "0 4px 14px -2px rgba(38,211,154,0.35)",
        "&:hover": {
          backgroundColor: theme.palette.primary.light,
          boxShadow: "0 6px 18px -2px rgba(38,211,154,0.45)"
        },
        "&:active": { transform: "scale(0.98)" }
      }),
      outlined: ({ theme }) => ({
        borderColor: theme.palette.divider,
        "&:hover": {
          borderColor: theme.palette.primary.main,
          backgroundColor: "transparent"
        }
      }),
      text: {
        paddingInline: 12,
        minHeight: 40
      }
    }
  },

  MuiIconButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.text.primary,
        borderRadius: 9999,
        transition: "background-color .15s ease",
        "&:hover": {
          backgroundColor: theme.palette.mode === "dark" ? "#1c2a47" : "#eef2f8"
        }
      })
    }
  },

  MuiChip: {
    styleOverrides: {
      root: { fontWeight: 600, borderRadius: 12 },
      filled: ({ theme }) => ({
        backgroundColor: theme.palette.mode === "dark" ? "#16213a" : "#eef2f8"
      }),
      colorSuccess: ({ theme }) => ({
        backgroundColor: "rgba(38,211,154,0.14)",
        color: theme.palette.primary.main,
        fontWeight: 700
      })
    }
  },

  MuiListItemButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: 16,
        "&.Mui-selected": {
          backgroundColor: theme.palette.mode === "dark"
            ? "rgba(38,211,154,0.10)"
            : "rgba(0,168,120,0.08)",
          color: theme.palette.primary.main,
          "&:hover": {
            backgroundColor: theme.palette.mode === "dark"
              ? "rgba(38,211,154,0.16)"
              : "rgba(0,168,120,0.12)"
          }
        }
      })
    }
  },

  MuiAppBar: {
    defaultProps: { color: "transparent", elevation: 0 },
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundImage: "none",
        backdropFilter: "blur(16px) saturate(160%)",
        WebkitBackdropFilter: "blur(16px) saturate(160%)",
        backgroundColor: theme.palette.mode === "dark"
          ? "rgba(12, 21, 37, 0.72)"
          : "rgba(245, 247, 251, 0.72)",
        borderBottom: "none"
      })
    }
  },

  MuiBottomNavigation: {
    styleOverrides: {
      root: ({ theme }) => ({
        backgroundColor: theme.palette.mode === "dark"
          ? "rgba(12, 21, 37, 0.92)"
          : "rgba(255,255,255,0.96)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        borderTop: `1px solid ${theme.palette.divider}`,
        height: 64,
        paddingBottom: "env(safe-area-inset-bottom)"
      })
    }
  },

  MuiBottomNavigationAction: {
    styleOverrides: {
      root: ({ theme }) => ({
        color: theme.palette.text.secondary,
        "&.Mui-selected": {
          color: theme.palette.primary.main
        }
      }),
      label: { fontSize: "0.6875rem", fontWeight: 600, marginTop: 4 }
    }
  },

  MuiDrawer: {
    styleOverrides: {
      paper: ({ theme }) => ({
        backgroundColor: theme.palette.background.default,
        borderRight: `1px solid ${theme.palette.divider}`
      })
    }
  },

  MuiTab: {
    styleOverrides: {
      root: { minHeight: 44, textTransform: "none", fontWeight: 600 }
    }
  },

  MuiTabs: {
    styleOverrides: {
      indicator: ({ theme }) => ({
        height: 3,
        borderRadius: 3,
        backgroundColor: theme.palette.primary.main
      })
    }
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: { borderRadius: 16 }
    }
  },

  MuiTextField: {
    defaultProps: { variant: "outlined", size: "small" }
  },

  MuiDialog: {
    styleOverrides: {
      paper: { borderRadius: 28 }
    }
  },

  MuiSnackbarContent: {
    styleOverrides: {
      root: { borderRadius: 16 }
    }
  },

  MuiAvatar: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontWeight: 700,
        backgroundColor: theme.palette.mode === "dark" ? "#1c2a47" : "#eef2f8",
        color: theme.palette.text.primary
      })
    }
  },

  MuiFab: {
    defaultProps: { color: "primary" },
    styleOverrides: {
      root: {
        borderRadius: 20,
        boxShadow: "0 8px 20px -4px rgba(38,211,154,0.45)",
        "&:hover": { boxShadow: "0 10px 24px -4px rgba(38,211,154,0.55)" }
      },
      extended: {
        height: 48,
        paddingInline: 20
      }
    }
  },

  MuiCircularProgress: {
    defaultProps: { size: 20, thickness: 5 }
  },

  MuiLinearProgress: {
    styleOverrides: {
      root: { borderRadius: 9999, height: 6 }
    }
  }
};
